import React, { useEffect, useState } from 'react';
import FlippexLoader from '../components/ui/FlippexLoader';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api, useAuth } from '../context/AuthContext';
import { ArrowRight, Compass } from '@phosphor-icons/react';
import { PortfolioValueHeader, NeedsAttention } from '../components/investor/PortfolioValueHeader';
import { InvestmentCard, OrderCard, ActivityFeed, ReturnsPanel } from '../components/investor/InvestmentCard';
import { greeting, money, pct } from '../components/investor/investorFormat';

const Panel = ({ title, action, children, testId }) => (
  <section className="bg-white rounded-2xl border border-black/[0.05]" data-testid={testId}>
    <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.05]">
      <h2 className="text-sm font-medium">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const LinkAction = ({ label, to }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="text-xs font-medium text-primary hover:opacity-70 inline-flex items-center gap-1"
    >
      {label} <ArrowRight size={12} weight="bold" />
    </button>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const safe = (p) => p.catch(() => ({ data: {} }));
    Promise.all([
      safe(api.get('/portfolio/v2/home')),
      safe(api.get('/projects')),
    ]).then(([home, projects]) => {
      const homeData = home.data || null;
      setData(homeData);
      const owned = new Set((homeData?.holdings || []).map((h) => h.project_id));
      const list = Array.isArray(projects.data) ? projects.data : (projects.data.projects || []);
      setOpportunities(list.filter((p) => !owned.has(p.id)).slice(0, 3));
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <FlippexLoader size={48} animate={false} />
      </div>
    );
  }

  const portfolio = data?.portfolio || {};
  const holdings = data?.holdings || [];
  const orders = data?.orders || [];
  const firstName = data?.investor?.first_name || user?.first_name || 'there';

  /* ---------- New investor experience ---------- */
  if (!data?.has_portfolio) {
    return (
      <div className="container mx-auto px-5 md:px-8 max-w-[1100px] py-10 md:py-14 space-y-8" data-testid="investor-home-new">
        <div>
            <p className="text-sm text-foreground-muted">{greeting()}, {firstName}</p>
            <h1 className="text-3xl md:text-4xl font-heading mt-2">Welcome to Flippex</h1>
            <p className="text-base text-foreground-muted mt-2 max-w-xl">
              Build a diversified real estate portfolio from carefully selected opportunities.
            </p>
            <Button className="mt-6 rounded-full px-6" onClick={() => navigate('/app/explore')} data-testid="explore-cta">
              Explore investments <ArrowRight size={15} weight="bold" className="ml-1.5" />
            </Button>
          </div>

          <NeedsAttention items={data?.attention} />

          {orders.length > 0 && (
            <Panel title="Your orders" testId="investor-orders">
              <div className="p-5 space-y-3">
                {orders.map((order) => <OrderCard key={order.id} order={order} />)}
              </div>
            </Panel>
          )}

          {opportunities.length > 0 && (
            <Panel title="Available opportunities" action={<LinkAction label="Explore all" to="/app/explore" />} testId="investor-opportunities">
              <div className="divide-y divide-black/[0.05]">
                {opportunities.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/app/explore/${p.id}`)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/70"
                    data-testid={`opportunity-${p.id}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{p.title}</span>
                      <span className="block text-xs text-foreground-muted truncate">
                        {[p.location, p.project_type?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="text-xs text-foreground-muted whitespace-nowrap">
                      {p.funding_progress ? `${Math.round(p.funding_progress)}% funded` : 'Open'}
                    </span>
                  </button>
                ))}
              </div>
            </Panel>
          )}
      </div>
    );
  }

  /* ---------- Active portfolio experience ---------- */
  const valuation = holdings[0];

  return (
    <div className="container mx-auto px-5 md:px-8 max-w-[1100px] py-8 md:py-12 space-y-8 md:space-y-10" data-testid="investor-home">
        <PortfolioValueHeader
          portfolio={portfolio}
          valuation={valuation}
          eyebrow={`${greeting()}, ${firstName}`}
        />

        <NeedsAttention items={data?.attention} />

        <Panel
          title="Your investments"
          action={<LinkAction label="View portfolio" to="/app/portfolio" />}
          testId="investor-investments"
        >
          <div className="p-4 md:p-5 space-y-3">
            {holdings.map((holding) => <InvestmentCard key={holding.security_id} holding={holding} />)}
            {orders.map((order) => <OrderCard key={order.id} order={order} />)}
          </div>
        </Panel>

        <div className="grid gap-4 md:gap-6 lg:grid-cols-[1.5fr_1fr]">
          <Panel title="Recent activity" action={<LinkAction label="View all" to="/app/activity" />} testId="investor-recent-activity">
            <ActivityFeed events={data?.activity} />
          </Panel>

          <Panel title="Returns" testId="investor-returns">
            <ReturnsPanel portfolio={portfolio} />
          </Panel>
        </div>

        {opportunities.length > 0 && (
          <Panel title="New opportunities" action={<LinkAction label="Explore all" to="/app/explore" />} testId="investor-opportunities">
            <div className="divide-y divide-black/[0.05]">
              {opportunities.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/app/explore/${p.id}`)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/70"
                  data-testid={`opportunity-${p.id}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{p.title}</span>
                    <span className="block text-xs text-foreground-muted truncate">
                      {[p.location, p.project_type?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="text-xs text-foreground-muted whitespace-nowrap">
                    {p.estimated_annual_return ? `${pct(p.estimated_annual_return)} projected` : money(p.min_investment)}
                  </span>
                </button>
              ))}
            </div>
          </Panel>
        )}
    </div>
  );
}
